import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Flex, 
  Container, 
  Button, 
  Heading, 
  Text,
  IconButton,
  VStack,
  HStack,
  AspectRatio,
  Spinner,
  useToast,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure
} from '@chakra-ui/react';
import { FaArrowLeft, FaExpand, FaCompress, FaGamepad, FaThumbtack } from 'react-icons/fa';
import { useParams, useNavigate } from 'react-router-dom';
import AppHeader from '../components/AppHeader';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';

// Interface for game details
interface GameDetails {
  id: string;
  name: string;
  description: string;
  embedId: string;
  system: string;
  color: string;
}

const GamePage: React.FC = () => {
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { isSubscriber, user } = useAuth();
  const { isOpen, onOpen, onClose } = useDisclosure();
  
  const [game, setGame] = useState<GameDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isPinMode, setIsPinMode] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // In a real app, you would fetch this data from an API
  // Here, we'll use the gameId directly from the URL
  useEffect(() => {
    const loadGameDetails = async () => {
      setIsLoading(true);
      try {
        // Simulate API fetch with setTimeout
        await new Promise(resolve => setTimeout(resolve, 500));
        
        if (!gameId) {
          setError("Game not found. Please check the URL or go back to the games list.");
          return;
        }
        
        // Extract the ID part from gameId (e.g., "42046-sonic-classic-heroes")
        const parts = gameId.split('-');
        const embedId = parts[0];
        const gameNameSlug = parts.slice(1).join('-');
        
        // Get the game system based on the embed ID pattern or range
        let system = "Retro Gaming";
        let color = "blue";
        
        // Determine system based on thumbnail patterns or ID ranges
        if (embedId.match(/^[0-9]+$/)) {
          // Numeric IDs - check ranges
          const idNum = parseInt(embedId, 10);
          if (idNum >= 40000 && idNum < 43000) {
            system = "Sega Genesis";
            color = "blue";
          } else if (idNum >= 30000 && idNum < 32000) {
            system = "SEGA Master System";
            color = "blue";
          } else if (idNum >= 20000 && idNum < 25000) {
            system = "Super Nintendo";
            color = "red";
          } else if (idNum >= 40000 && idNum < 42000) {
            system = "PlayStation";
            color = "gray";
          } else if (idNum >= 32000 && idNum < 33000) {
            system = "Nintendo 64";
            color = "purple";
          }
        } else {
          // String IDs - likely Arcade games
          system = "Arcade";
          color = "yellow";
          if (embedId.includes("sf2")) {
            system = "Arcade (Street Fighter)";
            color = "green";
          } else if (embedId.includes("bomb")) {
            system = "Arcade (Bomberman)";
            color = "red";
          }
        }
        
        // Format the name from the slug
        const formattedName = gameNameSlug
          .split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        
        // Create game object
        setGame({
          id: gameNameSlug,
          name: formattedName,
          description: `Play ${formattedName} in your browser. This classic ${system} game is available to play online directly in your browser.`,
          embedId,
          system,
          color
        });
      } catch (err) {
        console.error("Error loading game details:", err);
        setError("Failed to load game details. Please try again later.");
      } finally {
        setIsLoading(false);
      }
    };
    
    loadGameDetails();
  }, [gameId]);
  
  // Handle full screen toggle
  const toggleFullScreen = () => {
    const iframe = document.querySelector('iframe');
    if (!iframe) return;
    
    if (!isFullScreen) {
      if (iframe.requestFullscreen) {
        iframe.requestFullscreen();
      } else if ((iframe as any).webkitRequestFullscreen) { /* Safari */
        (iframe as any).webkitRequestFullscreen();
      } else if ((iframe as any).msRequestFullscreen) { /* IE11 */
        (iframe as any).msRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if ((document as any).webkitExitFullscreen) { /* Safari */
        (document as any).webkitExitFullscreen();
      } else if ((document as any).msExitFullscreen) { /* IE11 */
        (document as any).msExitFullscreen();
      }
    }
    
    setIsFullScreen(!isFullScreen);
  };
  
  // Listen for fullscreen change events
  useEffect(() => {
    const handleFullScreenChange = () => {
      const isCurrentlyFullScreen = !!(
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).msFullscreenElement
      );
      
      setIsFullScreen(isCurrentlyFullScreen);
    };
    
    document.addEventListener('fullscreenchange', handleFullScreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullScreenChange);
    document.addEventListener('msfullscreenchange', handleFullScreenChange);
    
    return () => {
      document.removeEventListener('fullscreenchange', handleFullScreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullScreenChange);
      document.removeEventListener('msfullscreenchange', handleFullScreenChange);
    };
  }, []);
  
  // Handle back button click
  const handleBackClick = () => {
    navigate('/games');
  };
  
  // Handle pin mode toggle
  const togglePinMode = () => {
    if (isSubscriber) {
      setIsPinMode(!isPinMode);
      toast({
        title: isPinMode ? "Pin Mode Disabled" : "Pin Mode Enabled",
        status: "info",
        duration: 2000,
        isClosable: true,
      });
    } else {
      onOpen();
    }
  };
  
  // Add an async function to load templates for the current game
  const loadTemplates = async () => {
    if (!user || !isSubscriber) {
      // Don't attempt to load templates for non-subscribers
      console.log("Templates are only available for subscribers");
      return [];
    }
    
    try {
      const { data, error } = await supabase
        .from('user_templates')
        .select('*')
        .order('priority', { ascending: false });
        
      if (error) {
        console.error('Error loading templates:', error);
        return [];
      }
      
      return data || [];
    } catch (error) {
      console.error('Failed to load templates:', error);
      return [];
    }
  };
  
  return (
    <Box 
      bg="#000" 
      color="white" 
      minHeight="100vh"
      height="100vh"
      overflow="hidden"
      bgGradient="linear(to-b, #1a1a2e, #16213e, #0f3460)"
      position="relative"
      _before={{
        content: '""',
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundImage: "url('https://images.unsplash.com/photo-1511512578047-dfb367046420?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2071&q=80')",
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        opacity: 0.08,
        zIndex: 0
      }}
    >
      {/* Header */}
      <AppHeader
        playbackState={null}
        layoutMode="split"
        setLayoutMode={() => {}}
        accentColor="#4A5568"
        pageTitle={game?.name || "Game"}
        isCondensed={false}
      />
      
      {/* Main Content */}
      <Box 
        as="main" 
        position="absolute"
        top="80px"
        bottom={0}
        left={0}
        right={0}
        overflowY="auto"
        zIndex={1}
      >
        <Container maxW="1200px" py={8} px={4}>
          {/* Navigation and controls */}
          <Flex 
            justify="space-between" 
            align="center"
            mb={6}
          >
            <Button
              leftIcon={<FaArrowLeft />}
              onClick={handleBackClick}
              variant="outline"
              colorScheme="blue"
              size="md"
            >
              Back to Games
            </Button>
            
            {game && (
              <Badge colorScheme={game.color} fontSize="md" px={3} py={1} borderRadius="md">
                {game.system}
              </Badge>
            )}
            
            <HStack spacing={3}>
              <Button
                leftIcon={<FaThumbtack />}
                onClick={togglePinMode}
                colorScheme="blue"
                variant="outline"
                isDisabled={isLoading || !!error}
              >
                {isPinMode ? "Exit Pin Mode" : "Pin Mode"}
              </Button>
              
              <Button
                leftIcon={isFullScreen ? <FaCompress /> : <FaExpand />}
                onClick={toggleFullScreen}
                colorScheme="blue"
                variant="outline"
                isDisabled={isLoading || !!error}
              >
                {isFullScreen ? "Exit Fullscreen" : "Fullscreen"}
              </Button>
            </HStack>
          </Flex>
          
          {/* Game title and description */}
          {game && (
            <VStack spacing={3} align="start" mb={6}>
              <Heading size="xl" color="white">
                {game.name}
              </Heading>
              <Text color="gray.300" fontSize="lg">
                {game.description}
              </Text>
            </VStack>
          )}
          
          {/* Game embed container */}
          <Box
            width="100%"
            bg="rgba(0,0,0,0.4)"
            backdropFilter="blur(10px)"
            borderWidth="1px"
            borderColor="whiteAlpha.100"
            borderRadius="lg"
            overflow="hidden"
            mb={6}
          >
            {isLoading ? (
              <Flex 
                justify="center" 
                align="center" 
                minHeight="500px"
                direction="column"
              >
                <Spinner size="xl" color="blue.400" mb={4} />
                <Text color="gray.400">Loading game...</Text>
              </Flex>
            ) : error ? (
              <Flex 
                justify="center" 
                align="center" 
                minHeight="500px"
                direction="column"
                p={8}
              >
                <Box 
                  as={FaGamepad} 
                  size="60px" 
                  color="gray.600" 
                  mb={6}
                />
                <Text color="red.400" fontSize="lg" mb={4} textAlign="center">
                  {error}
                </Text>
                <Button 
                  colorScheme="blue" 
                  onClick={handleBackClick}
                >
                  Return to Games
                </Button>
              </Flex>
            ) : (
              <AspectRatio ratio={4/3} width="100%">
                <iframe 
                  src={
                    game?.embedId.match(/^[0-9]+$/) 
                      ? `https://www.retrogames.cc/embed/${game.embedId}-${game.id}.html`
                      : `https://www.retrogames.cc/embed/${game?.embedId}.html`
                  }
                  frameBorder="no"
                  allowFullScreen={true}
                  title={game?.name}
                  scrolling="no"
                  style={{ width: '100%', height: '100%' }}
                />
              </AspectRatio>
            )}
          </Box>
          
          {/* Game instructions */}
          {game && (
            <Box
              p={6}
              bg="rgba(0,0,0,0.3)"
              borderRadius="lg"
              borderWidth="1px"
              borderColor="whiteAlpha.100"
            >
              <Heading size="md" mb={4} color="white">
                How to Play
              </Heading>
              <VStack spacing={3} align="start">
                <Text color="gray.300">
                  <strong>Controls:</strong> Use arrow keys for movement, Z, X, C for action buttons.
                </Text>
                <Text color="gray.300">
                  <strong>Full Screen:</strong> Click the expand icon in the top-right corner for a better experience.
                </Text>
                <Text color="gray.300">
                  <strong>Save/Load:</strong> Some games support save states using the emulator's built-in menu.
                </Text>
              </VStack>
            </Box>
          )}
        </Container>
      </Box>
      
      {/* Subscription Required Modal */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay backdropFilter="blur(10px)" />
        <ModalContent bg="gray.900" color="white" borderRadius="xl">
          <ModalHeader fontSize="xl">Pro Feature</ModalHeader>
          <ModalBody>
            <Text mb={4}>
              Pin Mode is only available for Pro subscribers. Upgrade to Pro to pin your favorite games and settings for quick access!
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" mr={3} onClick={onClose}>
              Maybe Later
            </Button>
            <Button colorScheme="blue" onClick={() => {
              onClose();
              navigate('/pricing');
            }}>
              View Pricing
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default GamePage; 